/* ============================================================================
 * BOOST CUSTOMISATION 2026-07-17 - Cart note persistence
 * ----------------------------------------------------------------------------
 * Makes the "Anything else we should know?" note actually save, and stay
 * consistent across the cart drawer and the cart page.
 *
 * The defect this fixes:
 *   - sections/side-cart.liquid renders the note textarea at lines ~1019-1024,
 *     in the GAP between the discount form (992-1010) and the checkout form
 *     (1031-1055). It is inside no form and carries no form= attribute, and no
 *     theme JS persisted it. Typing in the drawer note did nothing at all.
 *   - sections/main-cart.liquid binds its two textareas (desktop + mobile) to
 *     the cart form via form="{{ form_id }}", so the note only saved when that
 *     form was submitted (Update cart / Checkout). Typing then navigating, or
 *     opening the drawer, lost it.
 * Both surfaces already render {{ cart.note }} as their initial value, which is
 * why this looked like a sync bug: once cart.note is genuinely set, every
 * surface shows it. Nothing was setting it.
 *
 * What this does: saves the note to the cart via POST /cart/update.js on
 * change/blur (and debounced while typing), then mirrors the saved value into
 * every other note textarea on the page. No Liquid change is required; the
 * existing form= binding on the cart page stays as a harmless second path.
 *
 * Bonus: the Push to Draft Quote payload already sends cart.note to the
 * backend, which writes it onto the draft order. Once the note actually
 * persists, staff context typed in the cart flows through to the quote.
 *
 * Loaded globally in layout/theme.liquid because the cart drawer renders on
 * every page.
 * ==========================================================================*/
(function () {
  'use strict';

  var SAVE_DELAY = 600; // ms after the last keystroke before we persist
  var timer = null;
  var lastSaved = null; // the value we believe the cart currently holds

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function noteFields() {
    return Array.prototype.slice.call(document.querySelectorAll('textarea[name="note"]'));
  }

  function isNoteField(el) {
    return !!el && el.tagName === 'TEXTAREA' && el.name === 'note';
  }

  // Copy the saved value into the other note textareas (drawer, cart-page
  // desktop, cart-page mobile). Never touches the field being typed in.
  function mirror(value, except) {
    noteFields().forEach(function (t) {
      if (t === except) return;
      if (t === document.activeElement) return;
      if (t.value !== value) t.value = value;
    });
  }

  function save(value, source) {
    if (value === lastSaved) return; // nothing changed; don't hit the cart
    fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ note: value })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        // Trust the cart's echoed note over our local value.
        lastSaved = (cart && typeof cart.note === 'string') ? cart.note : value;
        mirror(lastSaved, source);
      })
      .catch(function () {
        // Non-fatal: the note simply is not saved. The field keeps its text so
        // the person can retry by blurring again. Never block the cart.
      });
  }

  function onInput(e) {
    if (!isNoteField(e.target)) return;
    var el = e.target;
    clearTimeout(timer);
    timer = setTimeout(function () { save(el.value, el); }, SAVE_DELAY);
  }

  // change and blur both persist immediately, so a note is saved before the
  // drawer re-renders (a re-render replaces the textarea and would otherwise
  // discard text still sitting inside the debounce window).
  function onCommit(e) {
    if (!isNoteField(e.target)) return;
    clearTimeout(timer);
    save(e.target.value, e.target);
  }

  // Seed lastSaved from what the server rendered, so focusing and leaving the
  // field without editing does not fire a pointless write.
  function init() {
    var first = noteFields()[0];
    if (first) lastSaved = first.value;
  }

  document.addEventListener('input', onInput, true);
  document.addEventListener('change', onCommit, true);
  document.addEventListener('blur', onCommit, true); // capture: blur does not bubble

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
